<!--
  LowResolutionCleanupAction -- the OPTIONAL way out of a low-resolution DTF
  placement (card #573, Jake 2026-10-29).

  The policy: 300 DPI or more says nothing; 200-299 warns; under 200 warns
  too; NOTHING is blocked and nothing is automatic. Beside the warning
  (LowResolutionNotice) this offers, for a raster DTF image under the
  recommended DPI (both low bands; never a vector or an unknown size, which
  the server answers with no verdict):

    * Cleanup switch ON  -> a "Send for art cleanup ($25)" button. The fee in
      the label is the Super Admin setting (never a constant). Clicking it
      only OPENS the existing sized send dialog (DigitizeAtSizeDialog) for
      this placement's size, where the fee is shown again and the
      acknowledgment is required. Shown to a caller who may send
      (distributor.digitization.send + distributor.decoration_library.write).
    * Cleanup switch OFF, or its read failed -> a note instead of a button:
      "This image is low resolution. For help with art cleanup, email
      <support email>." The address is the Super Admin `support-email`
      setting (default support@threadflow.com).

  While the switch is still being read neither shows (no flash of the note
  that then turns into a button). The server decides the verdict; this reads
  `resolution.method` and `resolution.tier` and recomputes nothing.
-->
<template>
    <div
        v-if="mode === 'button' || mode === 'support' || sent"
        class="lowres-cleanup"
    >
        <template v-if="sent">
            <p
                class="lowres-cleanup__note"
                role="status"
                data-testid="low-resolution-cleanup-sent"
            >
                Sent for art cleanup. Artistic Punch will return a cleaned-up
                version of this image.
            </p>
        </template>
        <button
            v-else-if="mode === 'button'"
            type="button"
            class="lowres-cleanup__btn"
            data-testid="low-resolution-cleanup-btn"
            @click="open"
        >
            {{ label }}
        </button>
        <p
            v-else
            class="lowres-cleanup__note"
            data-testid="low-resolution-cleanup-support"
        >
            This image is low resolution. For help with art cleanup, email
            <a :href="`mailto:${supportEmail}`">{{ supportEmail }}</a>.
        </p>

        <!--
          Teleported to the body: inside the Add placement wizard (a Vuetify
          v-dialog, whose content is layout-contained) a position:fixed
          dialog would be confined to the wizard card and its scrim would
          cover only that.
        -->
        <Teleport to="body">
            <DigitizeAtSizeDialog
                v-if="mode === 'button'"
                ref="dialog"
                :artwork-id="artworkId"
                :capabilities="capabilities"
                :trigger="false"
                fixed-method="dtf"
                :fixed-size="{ width, height }"
                @sent="onSent"
            />
        </Teleport>
    </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useAccountStore } from "@/stores/account";
import { useDtfArtCleanup } from "@/composables/useDtfArtCleanup";
import { useSupportEmail } from "@/composables/useSupportEmail";
import DigitizeAtSizeDialog from "@/pages/distributor/decoration-library/detail/DigitizeAtSizeDialog.vue";

const SEND_CAP = "distributor.digitization.send";
const LIBRARY_WRITE_CAP = "distributor.decoration_library.write";

const props = defineProps({
    // The server's verdict for this image at this size (null = no check).
    resolution: { type: Object, default: null },
    // The artwork the cleanup sends, and the placement's size in inches.
    artworkId: { type: Number, default: null },
    width: { type: Number, default: null },
    height: { type: Number, default: null },
});

const emit = defineEmits(["sent"]);

const account = useAccountStore();
const { cleanupEnabled, cleanupFee, loading } = useDtfArtCleanup();
const { supportEmail } = useSupportEmail();

const dialog = ref(null);
const sent = ref(false);

const capabilities = computed(() =>
    Array.isArray(account.capabilities) ? account.capabilities : [],
);
const maySend = computed(
    () =>
        capabilities.value.includes(SEND_CAP) &&
        capabilities.value.includes(LIBRARY_WRITE_CAP),
);

const positive = (n) => typeof n === "number" && Number.isFinite(n) && n > 0;

// A DTF raster under the RECOMMENDED dpi (soft or below the minimum). The
// server sends no verdict for vectors, unknown sizes or methods with no check.
const eligible = computed(() => {
    const r = props.resolution;
    return Boolean(
        r &&
            r.method === "dtf" &&
            (r.tier === "soft" || r.tier === "below_minimum") &&
            props.artworkId != null &&
            positive(props.width) &&
            positive(props.height),
    );
});

// 'button' | 'support' | null
const mode = computed(() => {
    if (!eligible.value || loading.value) return null;
    if (cleanupEnabled.value) return maySend.value ? "button" : null;
    return "support";
});

// "$25.00" -> "$25"; "$37.50" stays. No fee read -> no amount (the dialog
// still discloses it before anything is sent).
const label = computed(() => {
    const fee = cleanupFee.value;
    if (!fee) return "Send for art cleanup";
    const shown = fee.endsWith(".00") ? fee.slice(0, -3) : fee;
    return `Send for art cleanup ($${shown})`;
});

// "Sent" describes THIS image at THIS size; another placement starts fresh.
watch(
    () => [props.artworkId, props.width, props.height],
    () => {
        sent.value = false;
    },
);

function open() {
    dialog.value?.openDialog();
}

function onSent(payload) {
    sent.value = true;
    emit("sent", payload);
}
</script>

<style scoped>
.lowres-cleanup {
    margin: 6px 0 0;
}
.lowres-cleanup__btn {
    padding: 4px 10px;
    font-size: 12px;
    line-height: 1.4;
    border: 1px solid var(--tf-primary, #1976d2);
    border-radius: 4px;
    background: #fff;
    color: var(--tf-primary, #1976d2);
    cursor: pointer;
}
.lowres-cleanup__btn:hover {
    background: rgba(25, 118, 210, 0.08);
}
.lowres-cleanup__note {
    margin: 0;
    font-size: 12px;
    line-height: 1.4;
    color: var(--tf-fg-3, #4b5563);
}
</style>
